import gBerryProduct from '@/assets/g-berry-product.png';
import gAppleProduct from '@/assets/g-apple-product.png';
import gGrapefruitProduct from '@/assets/g-grapefruit-product.png';
import gSummerCrushProduct from '@/assets/g-summer-crush-product.png';
import gCranberryProduct from '@/assets/g-cranberry-product.png';
import gTonicWaterProduct from '@/assets/g-tonic-water-product.png';
import gKiwiStrawberryProduct from '@/assets/g-kiwi-strawberry-product.png';
import gBitterLemonProduct from '@/assets/g-bitter-lemon-product.png';
import gGingerAleProduct from '@/assets/g-ginger-ale-product.png';
import gCranberrySfProduct from '@/assets/g-cranberry-sf-product.png';
import gSodaWaterProduct from '@/assets/g-soda-water-product.png';
import gPinkLemonadeProduct from '@/assets/g-pink-lemonade-product.png';
import gLemonLimeProduct from '@/assets/g-lemon-lime-product.png';
import gCherryPopProduct from '@/assets/g-cherry-pop-product.png';
import gLemonLimeSfProduct from '@/assets/g-lemon-lime-sf-product.png';

export interface NutritionFact {
  label: string;
  value: string;
  unit?: string;
}

export interface ProductStats {
  stat1Value: string;
  stat1Label: string;
  stat2Value: string;
  stat2Label: string;
  stat3Value: string;
  stat3Label: string;
  hasArtificialColors: boolean;
  hasAddedSugar: boolean;
}

export interface DrinkVariant {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  themeColor: string;
  frameBaseUrl: string;
  frameDelayPattern?: '0.04s' | '0.041s' | '0.029s' | '0.042s';
  totalFrames: number;
  productImage: string;
  price: number;
  ingredients: Array<{ name: string; description: string }>;
  nutritionDescription: string;
  nutritionFacts: NutritionFact[];
  calories: number;
  servingSize: string;
  productStats: ProductStats;
  reviews: Array<{ name: string; rating: number; text: string; location: string }>;
  faqs: Array<{ question: string; answer: string }>;
}

export const DRINK_VARIANTS: DrinkVariant[] = [
  {
    id: 'berry',
    name: 'G',
    subtitle: 'BERRY FUSION',
    description: 'A bold, refreshing berry-flavored drink with a smooth finish and vibrant taste, distributed by DLC.',
    themeColor: '270 65% 40%',
    frameBaseUrl: 'https://munwvcsgdbxjtmgokqmi.supabase.co/storage/v1/object/public/Frames/ezgif-split/',
    frameDelayPattern: '0.04s',
    totalFrames: 192,
    productImage: gBerryProduct,
    price: 4,
    ingredients: [
      { name: 'Carbonated Water', description: 'Pure sparkling water base' },
      { name: 'Sugar', description: 'Natural sweetener (12g per serving)' },
      { name: 'Citric Acid (E330)', description: 'Acidity regulator for balanced taste' },
      { name: 'Sodium Benzoate (E211)', description: 'Preservative for freshness' },
      { name: 'Brilliant Blue FCF (E133)', description: 'Color additive' },
    ],
    nutritionDescription: 'Each 300ml can contains just 51 calories with 12g of carbohydrates. A refreshing treat with a balanced nutritional profile.',
    nutritionFacts: [
      { label: 'Serving Size', value: '1 can (300ml / 10.14 fl oz)' },
      { label: 'Calories', value: '51', unit: ' kcal' },
      { label: 'Protein', value: '<0.5', unit: 'g' },
      { label: 'Carbohydrate', value: '12', unit: 'g' },
      { label: 'Sugars', value: '12', unit: 'g' },
      { label: 'Fat', value: '<0.5', unit: 'g' },
      { label: 'Saturated Fat', value: '<0.1', unit: 'g' },
      { label: 'Fiber', value: '<0.5', unit: 'g' },
      { label: 'Salt', value: '<0.01', unit: 'g' },
    ],
    calories: 51,
    servingSize: '300ml',
    productStats: {
      stat1Value: '51',
      stat1Label: 'Calories/Can',
      stat2Value: '12g',
      stat2Label: 'Sugar/Can',
      stat3Value: '300ml',
      stat3Label: 'Per Can',
      hasArtificialColors: true,
      hasAddedSugar: true,
    },
    reviews: [
      { name: 'Sarah M.', rating: 5, text: "Finally, a berry drink that actually tastes like real berries! G has become my go-to refreshment. Love that it's sugar-free!", location: 'New York, NY' },
      { name: 'James K.', rating: 5, text: 'The bold flavor is incredible. I drink one every morning and it gives me the perfect start to my day without any crash.', location: 'Los Angeles, CA' },
      { name: 'Emily R.', rating: 5, text: 'Clean ingredients, amazing taste, and that purple can looks so good in my fridge. G Berry Fusion is everything I wanted in a drink.', location: 'Chicago, IL' },
      { name: 'Michael T.', rating: 4, text: 'Great taste and I appreciate the focus on natural ingredients. Would love to see more flavor variants from DLC!', location: 'Miami, FL' },
    ],
    faqs: [
      { question: 'What makes G Berry Fusion different from other berry beverages?', answer: 'G Berry Fusion is crafted with a blend of berry and strawberry flavors in sparkling carbonated water. Each 300ml can contains 51 calories and 12g of carbohydrates.' },
      { question: 'Is G Berry Fusion suitable for people with dietary restrictions?', answer: "G Berry Fusion is a carbonated beverage. Always check the ingredient list if you have specific allergies or dietary concerns." },
      { question: 'Where can I buy G Berry Fusion?', answer: 'G Berry Fusion is available at select retailers nationwide and through our online store. Visit our "Where to Buy" section or contact DLC distribution for information about stores in your area.' },
      { question: 'How should I store G Berry Fusion?', answer: 'For the best taste experience, store G Berry Fusion in a cool, dry place. Refrigerate before drinking for maximum refreshment. Once opened, consume within 24 hours for optimal freshness.' },
      { question: 'What is the serving size?', answer: 'Each can is 300ml (10.14 fl oz) and contains 51 calories.' },
      { question: 'Who distributes G Berry Fusion?', answer: 'G Berry Fusion is proudly distributed by DLC, a trusted name in premium food and beverage distribution.' },
    ],
  },
  {
    id: 'apple',
    name: 'G',
    subtitle: 'GREEN APPLE',
    description: 'A crisp, refreshing green apple drink with a tart finish and invigorating taste, distributed by DLC.',
    themeColor: '142 52% 24%',
    frameBaseUrl: 'https://munwvcsgdbxjtmgokqmi.supabase.co/storage/v1/object/public/Green%20Apple%20Frames/ezgif-split%20(1)/',
    frameDelayPattern: '0.04s',
    totalFrames: 240,
    productImage: gAppleProduct,
    price: 4,
    ingredients: [
      { name: 'Carbonated Water', description: 'Pure sparkling water base' },
      { name: 'Sugar', description: 'Natural sweetener' },
      { name: 'Sodium Benzoate', description: 'Preservative for freshness' },
      { name: 'Brilliant Blue FCF (E133)', description: 'Color additive' },
    ],
    nutritionDescription: 'Each 300ml can delivers 51 calories with 12g of carbohydrates and 0.5g of protein. Light and refreshing!',
    nutritionFacts: [
      { label: 'Serving Size', value: '1 can (300ml / 10.14 fl oz)' },
      { label: 'Calories', value: '51', unit: ' kcal' },
      { label: 'Protein', value: '0.5', unit: 'g' },
      { label: 'Carbohydrate', value: '12', unit: 'g' },
      { label: 'Fat', value: '<0.5', unit: 'g' },
      { label: 'Saturated Fat', value: '<0.1', unit: 'g' },
    ],
    calories: 51,
    servingSize: '300ml',
    productStats: {
      stat1Value: '51',
      stat1Label: 'Calories/Can',
      stat2Value: '12g',
      stat2Label: 'Sugar/Can',
      stat3Value: '300ml',
      stat3Label: 'Per Can',
      hasArtificialColors: true,
      hasAddedSugar: true,
    },
    reviews: [
      { name: 'Alex P.', rating: 5, text: "The green apple flavor is so authentic and refreshing! It's like biting into a fresh Granny Smith. Absolutely love it!", location: 'Seattle, WA' },
      { name: 'Jessica L.', rating: 5, text: 'Crisp, tart, and perfectly balanced. G Green Apple is my new favorite drink for hot summer days.', location: 'Austin, TX' },
      { name: 'David W.', rating: 5, text: 'That green can pops! The flavor is incredible - not too sweet, just the right amount of tartness.', location: 'Denver, CO' },
      { name: 'Rachel S.', rating: 4, text: 'Love the natural apple taste. Great to see a company focused on real ingredients and no artificial stuff.', location: 'Portland, OR' },
    ],
    faqs: [
      { question: 'What makes G Green Apple different from other apple beverages?', answer: 'G Green Apple is a crisp sparkling beverage with green apple flavor. Each 300ml can contains 51 calories and 12g of carbohydrates.' },
      { question: 'Is G Green Apple suitable for people with dietary restrictions?', answer: "G Green Apple is a carbonated beverage. Always check the ingredient list if you have specific allergies or dietary concerns." },
      { question: 'Where can I buy G Green Apple?', answer: 'G Green Apple is available at select retailers nationwide and through our online store. Visit our "Where to Buy" section or contact DLC distribution for information about stores in your area.' },
      { question: 'How should I store G Green Apple?', answer: 'For the best taste experience, store G Green Apple in a cool, dry place. Refrigerate before drinking for maximum refreshment. Once opened, consume within 24 hours for optimal freshness.' },
      { question: 'What is the serving size?', answer: 'Each can is 300ml (10.14 fl oz) and contains 51 calories.' },
      { question: 'Who distributes G Green Apple?', answer: 'G Green Apple is proudly distributed by DLC, a trusted name in premium food and beverage distribution.' },
    ],
  },
  {
    id: 'grapefruit',
    name: 'G',
    subtitle: 'GRAPEFRUIT',
    description: 'A zesty, refreshing grapefruit drink with a citrus kick and vibrant taste, distributed by DLC.',
    themeColor: '24 85% 50%',
    frameBaseUrl: 'https://munwvcsgdbxjtmgokqmi.supabase.co/storage/v1/object/public/Grapefruit%20Frames/ezgif-split/',
    frameDelayPattern: '0.041s',
    totalFrames: 240,
    productImage: gGrapefruitProduct,
    price: 4,
    ingredients: [
      { name: 'Carbonated Water', description: 'Pure sparkling water base' },
      { name: 'Sugar', description: 'Natural sweetener (7.5g per serving)' },
      { name: 'Citric Acid', description: 'Acidity regulator for balanced taste' },
      { name: 'Grapefruit Flavor', description: 'Natural citrus flavoring' },
      { name: 'Sodium Benzoate', description: 'Preservative for freshness' },
    ],
    nutritionDescription: 'Each 300ml can contains just 31 calories with 7.58g of carbohydrates. A light and refreshing citrus treat.',
    nutritionFacts: [
      { label: 'Serving Size', value: '1 can (300ml / 10.14 fl oz)' },
      { label: 'Calories', value: '31', unit: ' kcal' },
      { label: 'Protein', value: '0.01', unit: 'g' },
      { label: 'Carbohydrate', value: '7.58', unit: 'g' },
      { label: 'Sugars', value: '7.5', unit: 'g' },
      { label: 'Fat', value: '0.01', unit: 'g' },
      { label: 'Saturated Fat', value: '0.01', unit: 'g' },
      { label: 'Fiber', value: '0.02', unit: 'g' },
      { label: 'Sodium', value: '0.002', unit: 'g' },
    ],
    calories: 31,
    servingSize: '300ml',
    productStats: {
      stat1Value: '31',
      stat1Label: 'Calories/Can',
      stat2Value: '7.5g',
      stat2Label: 'Sugar/Can',
      stat3Value: '300ml',
      stat3Label: 'Per Can',
      hasArtificialColors: false,
      hasAddedSugar: true,
    },
    reviews: [
      { name: 'Chris D.', rating: 5, text: 'The grapefruit flavor is perfectly balanced - not too bitter, just refreshingly citrusy!', location: 'San Diego, CA' },
      { name: 'Amanda H.', rating: 5, text: 'Finally a grapefruit drink that tastes like real grapefruit. This is my new morning go-to!', location: 'Phoenix, AZ' },
      { name: 'Brian K.', rating: 5, text: 'Love the tangy citrus punch. The orange can really pops and the taste is even better.', location: 'Las Vegas, NV' },
      { name: 'Nicole P.', rating: 4, text: 'Great refreshing option for hot days. The grapefruit flavor is authentic and not overly sweet.', location: 'Tampa, FL' },
    ],
    faqs: [
      { question: 'What makes G Grapefruit different from other citrus beverages?', answer: 'G Grapefruit delivers an authentic grapefruit taste with only 31 calories per 300ml can and 7.5g of sugar.' },
      { question: 'Is G Grapefruit suitable for people with dietary restrictions?', answer: 'G Grapefruit is a carbonated beverage. Always check the ingredient list if you have specific allergies or dietary concerns.' },
      { question: 'Where can I buy G Grapefruit?', answer: 'G Grapefruit is available at select retailers nationwide and through our online store. Visit our "Where to Buy" section or contact DLC distribution for information about stores in your area.' },
      { question: 'How should I store G Grapefruit?', answer: 'For the best taste experience, store G Grapefruit in a cool, dry place. Refrigerate before drinking for maximum refreshment. Once opened, consume within 24 hours for optimal freshness.' },
      { question: 'What is the serving size?', answer: 'Each can is 300ml (10.14 fl oz) and contains 31 calories.' },
      { question: 'Who distributes G Grapefruit?', answer: 'G Grapefruit is proudly distributed by DLC, a trusted name in premium food and beverage distribution.' },
    ],
  },
  {
    id: 'summer-crush',
    name: 'G',
    subtitle: 'SUMMER CRUSH',
    description: 'A sweet, refreshing sugar-free watermelon drink with a juicy finish and vibrant summer taste, distributed by DLC.',
    themeColor: '0 72% 50%',
    frameBaseUrl: 'https://munwvcsgdbxjtmgokqmi.supabase.co/storage/v1/object/public/Summer%20Crush%20Frames/ezgif-split/',
    frameDelayPattern: '0.041s',
    totalFrames: 240,
    productImage: gSummerCrushProduct,
    price: 4,
    ingredients: [
      { name: 'Carbonated Water', description: 'Pure sparkling water base' },
      { name: 'Citric Acid (E330)', description: 'Acidity regulator for balanced taste' },
      { name: 'Sodium Benzoate & Potassium Sorbate', description: 'Preservatives for freshness' },
      { name: 'Sucralose (E955) & Acesulfame K (E950)', description: 'Zero-calorie sweeteners' },
      { name: 'Allura Red AC (E129)', description: 'Color additive' },
    ],
    nutritionDescription: 'Each 300ml can contains only 2.7 calories with virtually zero sugar. A guilt-free, refreshing watermelon treat.',
    nutritionFacts: [
      { label: 'Serving Size', value: '1 can (300ml / 10.14 fl oz)' },
      { label: 'Calories', value: '2.7', unit: ' kcal' },
      { label: 'Protein', value: '0.03', unit: 'g' },
      { label: 'Carbohydrate', value: '0.11', unit: 'g' },
      { label: 'Sugars', value: '0.05', unit: 'g' },
      { label: 'Fat', value: '0', unit: 'g' },
      { label: 'Saturated Fat', value: '0', unit: 'g' },
      { label: 'Fiber', value: '0.03', unit: 'g' },
      { label: 'Sodium', value: '0', unit: 'g' },
    ],
    calories: 3,
    servingSize: '300ml',
    productStats: {
      stat1Value: '3',
      stat1Label: 'Calories/Can',
      stat2Value: '0g',
      stat2Label: 'Sugar Free',
      stat3Value: '300ml',
      stat3Label: 'Per Can',
      hasArtificialColors: true,
      hasAddedSugar: false,
    },
    reviews: [
      { name: 'Jake M.', rating: 5, text: 'This tastes like summer in a can! The watermelon flavor is so refreshing and it\'s sugar-free!', location: 'Miami, FL' },
      { name: 'Sophia R.', rating: 5, text: 'Perfect poolside drink. Sweet, refreshing, zero sugar, and that red can looks amazing!', location: 'Los Angeles, CA' },
      { name: 'Marcus T.', rating: 5, text: 'Finally a sugar-free watermelon drink that actually tastes like real watermelon. Absolutely obsessed!', location: 'Houston, TX' },
      { name: 'Lily C.', rating: 4, text: 'Great summer flavor! Light and refreshing with zero sugar guilt. Love it!', location: 'San Diego, CA' },
    ],
    faqs: [
      { question: 'What makes G Summer Crush different from other watermelon beverages?', answer: 'G Summer Crush is completely sugar-free with only 3 calories per 300ml can, sweetened with sucralose and acesulfame K for a guilt-free refreshing experience.' },
      { question: 'Is G Summer Crush suitable for people with dietary restrictions?', answer: 'G Summer Crush is sugar-free and contains only 3 calories per can. It uses artificial sweeteners (sucralose E955, acesulfame K E950) and contains Allura Red AC color. Always check the ingredient list if you have specific allergies or dietary concerns.' },
      { question: 'Where can I buy G Summer Crush?', answer: 'G Summer Crush is available at select retailers nationwide and through our online store. Visit our "Where to Buy" section or contact DLC distribution for information about stores in your area.' },
      { question: 'How should I store G Summer Crush?', answer: 'For the best taste experience, store G Summer Crush in a cool, dry place. Refrigerate before drinking for maximum refreshment. Once opened, consume within 24 hours for optimal freshness.' },
      { question: 'What is the serving size?', answer: 'Each can is 300ml (10.14 fl oz) and contains only 3 calories with zero sugar.' },
      { question: 'Who distributes G Summer Crush?', answer: 'G Summer Crush is proudly distributed by DLC, a trusted name in premium food and beverage distribution.' },
    ],
  },
  {
    id: 'cranberry',
    name: 'G',
    subtitle: 'CRANBERRY',
    description: 'A tart, refreshing cranberry drink with a vibrant fruity finish, distributed by DLC.',
    themeColor: '350 70% 60%',
    frameBaseUrl: 'https://munwvcsgdbxjtmgokqmi.supabase.co/storage/v1/object/public/Cranberry%20Frames/cranberry/',
    frameDelayPattern: '0.029s',
    totalFrames: 279,
    productImage: gCranberryProduct,
    price: 4,
    ingredients: [
      { name: 'Carbonated Water', description: 'Pure sparkling water base' },
      { name: 'Sugar', description: 'Natural sweetener (41.4g per can)' },
      { name: 'Lemon Juice Concentrate', description: 'Natural citrus flavor' },
      { name: 'Cranberry Juice Concentrate', description: 'Real cranberry juice' },
      { name: 'Aronia Juice Concentrate', description: 'Antioxidant-rich berry juice' },
      { name: 'Citric Acid (E330)', description: 'Acidity regulator for balanced taste' },
      { name: 'Lime Juice Concentrate', description: 'Natural citrus flavor' },
      { name: 'Black Carrot Juice Concentrate', description: 'Natural color source' },
      { name: 'Potassium Sorbate (E202)', description: 'Preservative for freshness' },
      { name: 'Ascorbic Acid (E300)', description: 'Antioxidant (Vitamin C)' },
      { name: 'Cranberry Natural Flavoring', description: 'Natural cranberry essence' },
      { name: 'Anthocyanins (E163)', description: 'Natural color from berries' },
    ],
    nutritionDescription: 'Each 300ml can contains 171 calories with 41.4g of carbohydrates. A bold, fruity cranberry treat made with real fruit juice concentrates.',
    nutritionFacts: [
      { label: 'Serving Size', value: '1 can (300ml / 10.14 fl oz)' },
      { label: 'Calories', value: '171', unit: ' kcal' },
      { label: 'Protein', value: '<1.5', unit: 'g' },
      { label: 'Carbohydrate', value: '41.4', unit: 'g' },
      { label: 'Sugars', value: '41.4', unit: 'g' },
      { label: 'Fat', value: '<1.5', unit: 'g' },
      { label: 'Saturated Fat', value: '<0.3', unit: 'g' },
      { label: 'Fiber', value: '<1.5', unit: 'g' },
      { label: 'Sodium', value: '<0.03', unit: 'g' },
    ],
    calories: 171,
    servingSize: '300ml',
    productStats: {
      stat1Value: '171',
      stat1Label: 'Calories/Can',
      stat2Value: '41.4g',
      stat2Label: 'Sugar/Can',
      stat3Value: '300ml',
      stat3Label: 'Per Can',
      hasArtificialColors: false,
      hasAddedSugar: true,
    },
    reviews: [
      { name: 'Laura B.', rating: 5, text: 'The cranberry flavor is perfectly tart and refreshing! Love that it uses real fruit juice concentrates.', location: 'Boston, MA' },
      { name: 'Kevin R.', rating: 5, text: 'Love the beautiful natural color from real berries. Bold cranberry taste!', location: 'Minneapolis, MN' },
      { name: 'Sandra M.', rating: 5, text: 'Perfect balance of sweet and tart with real cranberry juice. My new favorite!', location: 'Seattle, WA' },
      { name: 'Tom H.', rating: 4, text: 'Great authentic cranberry flavor with no artificial colors. Refreshing!', location: 'Detroit, MI' },
    ],
    faqs: [
      { question: 'What makes G Cranberry different from other cranberry beverages?', answer: 'G Cranberry is made with real cranberry, lemon, lime, and aronia juice concentrates. Each 300ml can contains 171 calories and uses natural anthocyanins for color - no artificial colors.' },
      { question: 'Is G Cranberry suitable for people with dietary restrictions?', answer: 'G Cranberry contains real fruit juice concentrates and natural colors. Always check the ingredient list if you have specific allergies or dietary concerns.' },
      { question: 'Where can I buy G Cranberry?', answer: 'G Cranberry is available at select retailers nationwide and through our online store. Visit our "Where to Buy" section or contact DLC distribution for information about stores in your area.' },
      { question: 'How should I store G Cranberry?', answer: 'For the best taste experience, store G Cranberry in a cool, dry place. Refrigerate before drinking for maximum refreshment.' },
      { question: 'What is the serving size?', answer: 'Each can is 300ml (10.14 fl oz) and contains 171 calories with 41.4g of sugar.' },
      { question: 'Who distributes G Cranberry?', answer: 'G Cranberry is proudly distributed by DLC, a trusted name in premium food and beverage distribution.' },
    ],
  },
  {
    id: 'tonic-water',
    name: 'G',
    subtitle: 'TONIC WATER',
    description: 'A crisp, classic tonic water with a subtle bitter finish and refreshing taste, distributed by DLC.',
    themeColor: '45 80% 55%',
    frameBaseUrl: 'https://munwvcsgdbxjtmgokqmi.supabase.co/storage/v1/object/public/Tonic%20Water%20Frames/tonicwater/',
    frameDelayPattern: '0.041s',
    totalFrames: 192,
    productImage: gTonicWaterProduct,
    price: 4,
    ingredients: [
      { name: 'Carbonated Water', description: 'Pure sparkling water base' },
      { name: 'Sugar', description: 'Natural sweetener (23g per can)' },
      { name: 'Citric Acid (E330)', description: 'Acidity regulator for balanced taste' },
      { name: 'Quinine Flavoring', description: 'Natural and artificial bitter flavoring' },
      { name: 'Sodium Benzoate (E211)', description: 'Preservative for freshness' },
    ],
    nutritionDescription: 'Each 300ml can contains 99 calories with 23g of carbohydrates. A crisp, classic tonic water with authentic quinine flavor.',
    nutritionFacts: [
      { label: 'Serving Size', value: '1 can (300ml / 10.14 fl oz)' },
      { label: 'Calories', value: '99', unit: ' kcal' },
      { label: 'Protein', value: '<1.5', unit: 'g' },
      { label: 'Carbohydrate', value: '23.04', unit: 'g' },
      { label: 'Sugars', value: '23.04', unit: 'g' },
      { label: 'Fat', value: '<1.5', unit: 'g' },
      { label: 'Saturated Fat', value: '<0.3', unit: 'g' },
      { label: 'Fiber', value: '<1.5', unit: 'g' },
      { label: 'Sodium', value: '<0.015', unit: 'g' },
      { label: 'Salt', value: '<0.054', unit: 'g' },
    ],
    calories: 99,
    servingSize: '300ml',
    productStats: {
      stat1Value: '99',
      stat1Label: 'Calories/Can',
      stat2Value: '23g',
      stat2Label: 'Sugar/Can',
      stat3Value: '300ml',
      stat3Label: 'Per Can',
      hasArtificialColors: false,
      hasAddedSugar: true,
    },
    reviews: [
      { name: 'Richard P.', rating: 5, text: 'The perfect tonic water - crisp, balanced, and refreshing with authentic quinine!', location: 'London, UK' },
      { name: 'Catherine L.', rating: 5, text: 'Great for cocktails or on its own. Love the subtle bitter finish.', location: 'New York, NY' },
      { name: 'George W.', rating: 5, text: 'Finally a tonic water that tastes premium. The golden can looks fantastic!', location: 'Chicago, IL' },
      { name: 'Helen K.', rating: 4, text: 'Smooth and sophisticated. Perfect with a slice of lime.', location: 'San Francisco, CA' },
    ],
    faqs: [
      { question: 'What makes G Tonic Water different from other tonic waters?', answer: 'G Tonic Water delivers a premium tonic experience with authentic quinine flavoring. Each 300ml can contains 99 calories.' },
      { question: 'Is G Tonic Water suitable for people with dietary restrictions?', answer: 'G Tonic Water is a carbonated beverage containing quinine flavoring (natural and artificial). Always check the ingredient list if you have specific allergies or dietary concerns.' },
      { question: 'Where can I buy G Tonic Water?', answer: 'G Tonic Water is available at select retailers nationwide and through our online store. Visit our "Where to Buy" section or contact DLC distribution for information about stores in your area.' },
      { question: 'How should I store G Tonic Water?', answer: 'For the best taste experience, store G Tonic Water in a cool, dry place. Refrigerate before drinking for maximum refreshment.' },
      { question: 'What is the serving size?', answer: 'Each can is 300ml (10.14 fl oz) and contains 99 calories with 23g of sugar.' },
      { question: 'Who distributes G Tonic Water?', answer: 'G Tonic Water is proudly distributed by DLC, a trusted name in premium food and beverage distribution.' },
    ],
  },
  {
    id: 'kiwi-strawberry',
    name: 'G',
    subtitle: 'KIWI STRAWBERRY',
    description: 'A refreshing sugar-free blend of kiwi and strawberry flavors with a tropical finish, distributed by DLC.',
    themeColor: '120 45% 55%', // Light green
    frameBaseUrl: 'https://munwvcsgdbxjtmgokqmi.supabase.co/storage/v1/object/public/Kiwi%20Strawberry%20Frames/ks/',
    frameDelayPattern: '0.042s',
    totalFrames: 192,
    productImage: gKiwiStrawberryProduct,
    price: 4,
    ingredients: [
      { name: 'Carbonated Water', description: 'Pure sparkling water base' },
      { name: 'Citric Acid (E330)', description: 'Acidity regulator for balanced taste' },
      { name: 'Kiwi Artificial Flavoring', description: 'Tropical kiwi flavor' },
      { name: 'Strawberry Artificial Flavoring', description: 'Sweet strawberry flavor' },
      { name: 'Potassium Sorbate (E202)', description: 'Preservative for freshness' },
      { name: 'Sodium Benzoate (E211)', description: 'Preservative for freshness' },
      { name: 'Sucralose (E955)', description: 'Zero-calorie sweetener' },
      { name: 'Acesulfame K (E950)', description: 'Zero-calorie sweetener' },
      { name: 'Allura Red AC (E129)', description: 'Color additive' },
    ],
    nutritionDescription: 'Each 300ml can is sugar-free with only 4.5 calories. A guilt-free tropical refreshment sweetened with sucralose and acesulfame K.',
    nutritionFacts: [
      { label: 'Serving Size', value: '1 can (300ml / 10.14 fl oz)' },
      { label: 'Calories', value: '4.5', unit: ' kcal' },
      { label: 'Protein', value: '0.03', unit: 'g' },
      { label: 'Carbohydrate', value: '0.12', unit: 'g' },
      { label: 'Sugars', value: '0.03', unit: 'g' },
      { label: 'Fat', value: '0.03', unit: 'g' },
      { label: 'Saturated Fat', value: '0', unit: 'g' },
      { label: 'Sodium', value: '0.009', unit: 'g' },
    ],
    calories: 5,
    servingSize: '300ml',
    productStats: {
      stat1Value: '5',
      stat1Label: 'Calories/Can',
      stat2Value: '0g',
      stat2Label: 'Sugar Free',
      stat3Value: '300ml',
      stat3Label: 'Per Can',
      hasArtificialColors: true,
      hasAddedSugar: false,
    },
    reviews: [
      { name: 'Emma T.', rating: 5, text: 'The kiwi-strawberry combo is absolutely delicious and it\'s sugar-free! My new favorite!', location: 'Auckland, NZ' },
      { name: 'Ryan S.', rating: 5, text: 'Tropical vibes in a can. Love the refreshing taste without the sugar guilt.', location: 'Sydney, AU' },
      { name: 'Mia L.', rating: 5, text: 'Perfect blend of sweet and tangy. The green can looks so fresh!', location: 'Vancouver, CA' },
      { name: 'Josh K.', rating: 4, text: 'Great sugar-free option with an authentic fruity taste. Very refreshing!', location: 'Miami, FL' },
    ],
    faqs: [
      { question: 'What makes G Kiwi Strawberry different from other fruity beverages?', answer: 'G Kiwi Strawberry is completely sugar-free with only 5 calories per 300ml can, sweetened with sucralose and acesulfame K for a guilt-free tropical experience.' },
      { question: 'Is G Kiwi Strawberry suitable for people with dietary restrictions?', answer: 'G Kiwi Strawberry is sugar-free and uses artificial sweeteners (sucralose E955, acesulfame K E950). It contains Allura Red AC color. Always check the ingredient list if you have specific allergies or dietary concerns.' },
      { question: 'Where can I buy G Kiwi Strawberry?', answer: 'G Kiwi Strawberry is available at select retailers nationwide and through our online store. Visit our "Where to Buy" section or contact DLC distribution.' },
      { question: 'How should I store G Kiwi Strawberry?', answer: 'For the best taste experience, store G Kiwi Strawberry in a cool, dry place. Refrigerate before drinking for maximum refreshment.' },
      { question: 'What is the serving size?', answer: 'Each can is 300ml (10.14 fl oz) and contains only 5 calories with zero sugar.' },
      { question: 'Who distributes G Kiwi Strawberry?', answer: 'G Kiwi Strawberry is proudly distributed by DLC, a trusted name in premium food and beverage distribution.' },
    ],
  },
  {
    id: 'bitter-lemon',
    name: 'G',
    subtitle: 'BITTER LEMON',
    description: 'A zesty, refreshing bitter lemon drink with a citrus punch and sophisticated finish, distributed by DLC.',
    themeColor: '75 70% 50%', // Yellow-green
    frameBaseUrl: 'https://munwvcsgdbxjtmgokqmi.supabase.co/storage/v1/object/public/Bitter%20Lemon%20Frames/bl/',
    frameDelayPattern: '0.042s',
    totalFrames: 192,
    productImage: gBitterLemonProduct,
    price: 4,
    ingredients: [
      { name: 'Carbonated Water', description: 'Pure sparkling water base' },
      { name: 'Sugar', description: 'Natural sweetener (44.4g per can)' },
      { name: 'Citric Acid (E330)', description: 'Acidity regulator for balanced taste' },
      { name: 'Lemon Flavoring', description: 'Natural and artificial lemon flavor' },
      { name: 'Quinine', description: 'Classic bitter flavoring' },
      { name: 'Sodium Benzoate (E211)', description: 'Preservative for freshness' },
    ],
    nutritionDescription: 'Each 300ml can contains 182 calories with 44.4g of carbohydrates. A bold, zesty bitter lemon experience.',
    nutritionFacts: [
      { label: 'Serving Size', value: '1 can (300ml / 10.14 fl oz)' },
      { label: 'Calories', value: '182', unit: ' kcal' },
      { label: 'Protein', value: '0.06', unit: 'g' },
      { label: 'Carbohydrate', value: '44.4', unit: 'g' },
      { label: 'Sugars', value: '44.4', unit: 'g' },
      { label: 'Fat', value: '0.03', unit: 'g' },
      { label: 'Saturated Fat', value: '0.006', unit: 'g' },
      { label: 'Fiber', value: '0.03', unit: 'g' },
      { label: 'Sodium', value: '0.042', unit: 'g' },
      { label: 'Salt', value: '0.105', unit: 'g' },
    ],
    calories: 182,
    servingSize: '300ml',
    productStats: {
      stat1Value: '182',
      stat1Label: 'Calories/Can',
      stat2Value: '44.4g',
      stat2Label: 'Sugar/Can',
      stat3Value: '300ml',
      stat3Label: 'Per Can',
      hasArtificialColors: false,
      hasAddedSugar: true,
    },
    reviews: [
      { name: 'Oliver M.', rating: 5, text: 'The perfect bitter lemon - zesty, refreshing, and sophisticated!', location: 'London, UK' },
      { name: 'Sophie R.', rating: 5, text: 'Love the authentic bitter lemon taste. Great on its own or as a mixer.', location: 'Paris, FR' },
      { name: 'Daniel H.', rating: 5, text: 'Refreshingly bitter with a perfect citrus kick. The yellow-green can is gorgeous!', location: 'Berlin, DE' },
      { name: 'Anna K.', rating: 4, text: 'Classic bitter lemon flavor done right. Very refreshing on a hot day.', location: 'Rome, IT' },
    ],
    faqs: [
      { question: 'What makes G Bitter Lemon different from other bitter lemon beverages?', answer: 'G Bitter Lemon delivers an authentic bitter lemon experience with 182 calories per 300ml can and a perfect balance of citrus and quinine bitterness.' },
      { question: 'Is G Bitter Lemon suitable for people with dietary restrictions?', answer: 'G Bitter Lemon contains sugar (44.4g per can) and quinine flavoring. Always check the ingredient list if you have specific allergies or dietary concerns.' },
      { question: 'Where can I buy G Bitter Lemon?', answer: 'G Bitter Lemon is available at select retailers nationwide and through our online store. Visit our "Where to Buy" section or contact DLC distribution.' },
      { question: 'How should I store G Bitter Lemon?', answer: 'For the best taste experience, store G Bitter Lemon in a cool, dry place. Refrigerate before drinking for maximum refreshment.' },
      { question: 'What is the serving size?', answer: 'Each can is 300ml (10.14 fl oz) and contains 182 calories with 44.4g of sugar.' },
      { question: 'Who distributes G Bitter Lemon?', answer: 'G Bitter Lemon is proudly distributed by DLC, a trusted name in premium food and beverage distribution.' },
    ],
  },
  {
    id: 'gingerale',
    name: 'G',
    subtitle: 'GINGER ALE',
    description: 'A classic, refreshing ginger ale with a smooth ginger finish and crisp taste, distributed by DLC.',
    themeColor: '158 92% 68%', // #63F7B4 converted to HSL
    frameBaseUrl: 'https://munwvcsgdbxjtmgokqmi.supabase.co/storage/v1/object/public/Gingerale%20Frames/ginger/',
    frameDelayPattern: '0.041s',
    totalFrames: 192,
    productImage: gGingerAleProduct,
    price: 4,
    ingredients: [
      { name: 'Carbonated Water', description: 'Pure sparkling water base' },
      { name: 'Sugar', description: 'Natural sweetener (30.6g per can)' },
      { name: 'Citric Acid (E330)', description: 'Acidity regulator for balanced taste' },
      { name: 'Ginger Flavoring', description: 'Natural and artificial ginger flavor' },
      { name: 'Sodium Benzoate (E211)', description: 'Preservative for freshness' },
    ],
    nutritionDescription: 'Each 300ml can contains 123 calories with 30.6g of carbohydrates. A smooth, classic ginger ale experience.',
    nutritionFacts: [
      { label: 'Serving Size', value: '1 can (300ml / 10.14 fl oz)' },
      { label: 'Calories', value: '123', unit: ' kcal' },
      { label: 'Protein', value: '0', unit: 'g' },
      { label: 'Carbohydrate', value: '30.6', unit: 'g' },
      { label: 'Sugars', value: '30.6', unit: 'g' },
      { label: 'Fat', value: '0', unit: 'g' },
      { label: 'Saturated Fat', value: '0', unit: 'g' },
      { label: 'Fiber', value: '0', unit: 'g' },
      { label: 'Sodium', value: '0.012', unit: 'g' },
      { label: 'Salt', value: '0.03', unit: 'g' },
    ],
    calories: 123,
    servingSize: '300ml',
    productStats: {
      stat1Value: '123',
      stat1Label: 'Calories/Can',
      stat2Value: '30.6g',
      stat2Label: 'Sugar/Can',
      stat3Value: '300ml',
      stat3Label: 'Per Can',
      hasArtificialColors: false,
      hasAddedSugar: true,
    },
    reviews: [
      { name: 'William B.', rating: 5, text: 'The smoothest ginger ale I\'ve ever tasted. Perfect balance of sweetness and ginger spice!', location: 'Toronto, CA' },
      { name: 'Grace L.', rating: 5, text: 'Classic ginger ale flavor with a refreshing finish. Love it!', location: 'Dublin, IE' },
      { name: 'Henry P.', rating: 5, text: 'Great mixer or on its own. The ginger flavor is just right - not too strong.', location: 'Melbourne, AU' },
      { name: 'Lucy M.', rating: 4, text: 'Refreshing and smooth with authentic ginger taste. My go-to ginger ale now!', location: 'Auckland, NZ' },
    ],
    faqs: [
      { question: 'What makes G Ginger Ale different from other ginger ale beverages?', answer: 'G Ginger Ale delivers a smooth, classic ginger ale experience with 123 calories per 300ml can and perfectly balanced sweetness and ginger spice.' },
      { question: 'Is G Ginger Ale suitable for people with dietary restrictions?', answer: 'G Ginger Ale contains sugar (30.6g per can). Always check the ingredient list if you have specific allergies or dietary concerns.' },
      { question: 'Where can I buy G Ginger Ale?', answer: 'G Ginger Ale is available at select retailers nationwide and through our online store. Visit our "Where to Buy" section or contact DLC distribution.' },
      { question: 'How should I store G Ginger Ale?', answer: 'For the best taste experience, store G Ginger Ale in a cool, dry place. Refrigerate before drinking for maximum refreshment.' },
      { question: 'What is the serving size?', answer: 'Each can is 300ml (10.14 fl oz) and contains 123 calories with 30.6g of sugar.' },
      { question: 'Who distributes G Ginger Ale?', answer: 'G Ginger Ale is proudly distributed by DLC, a trusted name in premium food and beverage distribution.' },
    ],
  },
  {
    id: 'cranberry-sf',
    name: 'G',
    subtitle: 'CRANBERRY SUGAR FREE',
    description: 'A tart, refreshing sugar-free cranberry drink with zero sugar guilt, distributed by DLC.',
    themeColor: '350 70% 85%',
    frameBaseUrl: 'https://munwvcsgdbxjtmgokqmi.supabase.co/storage/v1/object/public/Cranberry%20Sugarfree%20Frames/ezgif-split/',
    frameDelayPattern: '0.041s',
    totalFrames: 192,
    productImage: gCranberrySfProduct,
    price: 4,
    ingredients: [
      { name: 'Carbonated Water', description: 'Pure sparkling water base' },
      { name: 'Citric Acid (E330)', description: 'Acidity regulator for balanced taste' },
      { name: 'Cranberry Flavoring', description: 'Natural cranberry flavor' },
      { name: 'Potassium Sorbate (E202)', description: 'Preservative for freshness' },
      { name: 'Sodium Benzoate (E211)', description: 'Preservative for freshness' },
      { name: 'Sucralose (E955)', description: 'Zero-calorie sweetener' },
      { name: 'Acesulfame K (E950)', description: 'Zero-calorie sweetener' },
      { name: 'Allura Red AC (E129)', description: 'Color additive' },
    ],
    nutritionDescription: 'Each 300ml can is sugar-free with only 5 calories. A guilt-free cranberry refreshment sweetened with sucralose and acesulfame K.',
    nutritionFacts: [
      { label: 'Serving Size', value: '1 can (300ml / 10.14 fl oz)' },
      { label: 'Calories', value: '5', unit: ' kcal' },
      { label: 'Protein', value: '0.03', unit: 'g' },
      { label: 'Carbohydrate', value: '0.12', unit: 'g' },
      { label: 'Sugars', value: '0', unit: 'g' },
      { label: 'Fat', value: '0', unit: 'g' },
      { label: 'Saturated Fat', value: '0', unit: 'g' },
      { label: 'Sodium', value: '0.009', unit: 'g' },
    ],
    calories: 5,
    servingSize: '300ml',
    productStats: {
      stat1Value: '5',
      stat1Label: 'Calories/Can',
      stat2Value: '0g',
      stat2Label: 'Sugar Free',
      stat3Value: '300ml',
      stat3Label: 'Per Can',
      hasArtificialColors: true,
      hasAddedSugar: false,
    },
    reviews: [
      { name: 'Michelle P.', rating: 5, text: 'All the tart cranberry flavor without the sugar! This is my new favorite guilt-free drink.', location: 'Boston, MA' },
      { name: 'Andrew K.', rating: 5, text: 'Perfect for those watching their sugar intake. The cranberry taste is authentic and refreshing!', location: 'Seattle, WA' },
      { name: 'Jennifer L.', rating: 5, text: 'Love that I can enjoy cranberry flavor without worrying about calories. The light pink can is gorgeous!', location: 'Portland, OR' },
      { name: 'Steven M.', rating: 4, text: 'Great sugar-free alternative to regular cranberry drinks. Tart and refreshing!', location: 'Denver, CO' },
    ],
    faqs: [
      { question: 'What makes G Cranberry Sugar Free different from regular G Cranberry?', answer: 'G Cranberry Sugar Free is completely sugar-free with only 5 calories per 300ml can, sweetened with sucralose and acesulfame K instead of sugar.' },
      { question: 'Is G Cranberry Sugar Free suitable for people with dietary restrictions?', answer: 'G Cranberry Sugar Free is sugar-free and uses artificial sweeteners (sucralose E955, acesulfame K E950). Always check the ingredient list if you have specific allergies or dietary concerns.' },
      { question: 'Where can I buy G Cranberry Sugar Free?', answer: 'G Cranberry Sugar Free is available at select retailers nationwide and through our online store. Visit our "Where to Buy" section or contact DLC distribution.' },
      { question: 'How should I store G Cranberry Sugar Free?', answer: 'For the best taste experience, store G Cranberry Sugar Free in a cool, dry place. Refrigerate before drinking for maximum refreshment.' },
      { question: 'What is the serving size?', answer: 'Each can is 300ml (10.14 fl oz) and contains only 5 calories with zero sugar.' },
      { question: 'Who distributes G Cranberry Sugar Free?', answer: 'G Cranberry Sugar Free is proudly distributed by DLC, a trusted name in premium food and beverage distribution.' },
    ],
  },
  {
    id: 'soda-water',
    name: 'G',
    subtitle: 'SODA WATER',
    description: 'Pure, crisp carbonated water with zero calories and zero sugar, distributed by DLC.',
    themeColor: '0 0% 55%',
    frameBaseUrl: 'https://munwvcsgdbxjtmgokqmi.supabase.co/storage/v1/object/public/Soda%20Water%20Frames/ezgif-split%20(1)/',
    frameDelayPattern: '0.041s',
    totalFrames: 192,
    productImage: gSodaWaterProduct,
    price: 4,
    ingredients: [
      { name: 'Carbonated Water', description: 'Pure sparkling water' },
      { name: 'Sodium Bicarbonate', description: 'Natural mineral for crisp taste' },
    ],
    nutritionDescription: 'Each 300ml can contains zero calories and zero sugar. Pure, refreshing carbonated water for any occasion.',
    nutritionFacts: [
      { label: 'Serving Size', value: '1 can (300ml / 10.14 fl oz)' },
      { label: 'Calories', value: '0', unit: ' kcal' },
      { label: 'Protein', value: '0', unit: 'g' },
      { label: 'Carbohydrate', value: '0', unit: 'g' },
      { label: 'Sugars', value: '0', unit: 'g' },
      { label: 'Fat', value: '0', unit: 'g' },
      { label: 'Saturated Fat', value: '0', unit: 'g' },
      { label: 'Sodium', value: '0.05', unit: 'g' },
    ],
    calories: 0,
    servingSize: '300ml',
    productStats: {
      stat1Value: '0',
      stat1Label: 'Calories/Can',
      stat2Value: '0g',
      stat2Label: 'Zero Sugar',
      stat3Value: '300ml',
      stat3Label: 'Per Can',
      hasArtificialColors: false,
      hasAddedSugar: false,
    },
    reviews: [
      { name: 'Thomas R.', rating: 5, text: 'The purest soda water I\'ve found. Perfect for mixing or drinking straight. Zero everything!', location: 'New York, NY' },
      { name: 'Rebecca S.', rating: 5, text: 'Crisp, clean, and refreshing. Love the sleek grey can design!', location: 'Los Angeles, CA' },
      { name: 'Christopher D.', rating: 5, text: 'Finally a quality soda water in a convenient can. Great carbonation that lasts.', location: 'Chicago, IL' },
      { name: 'Amanda W.', rating: 4, text: 'Simple and pure. Perfect base for cocktails or just hydrating with bubbles.', location: 'Miami, FL' },
    ],
    faqs: [
      { question: 'What makes G Soda Water different from other soda waters?', answer: 'G Soda Water is pure carbonated water with zero calories, zero sugar, and zero artificial ingredients. Just crisp, refreshing bubbles.' },
      { question: 'Is G Soda Water suitable for people with dietary restrictions?', answer: 'G Soda Water contains only carbonated water and sodium bicarbonate. It is suitable for most dietary requirements. Always check the ingredient list if you have specific concerns.' },
      { question: 'Where can I buy G Soda Water?', answer: 'G Soda Water is available at select retailers nationwide and through our online store. Visit our "Where to Buy" section or contact DLC distribution.' },
      { question: 'How should I store G Soda Water?', answer: 'For the best taste experience, store G Soda Water in a cool, dry place. Refrigerate before drinking for maximum refreshment.' },
      { question: 'What is the serving size?', answer: 'Each can is 300ml (10.14 fl oz) and contains zero calories and zero sugar.' },
      { question: 'Who distributes G Soda Water?', answer: 'G Soda Water is proudly distributed by DLC, a trusted name in premium food and beverage distribution.' },
    ],
  },
  {
    id: 'pink-lemonade',
    name: 'G',
    subtitle: 'PINK LEMONADE',
    description: 'A sweet, refreshing pink lemonade with a vibrant citrus taste, distributed by DLC.',
    themeColor: '330 80% 55%',
    frameBaseUrl: 'https://munwvcsgdbxjtmgokqmi.supabase.co/storage/v1/object/public/Pink%20Lemonade%20Frames/ezgif-split%20(3)/',
    frameDelayPattern: '0.041s',
    totalFrames: 192,
    productImage: gPinkLemonadeProduct,
    price: 4,
    ingredients: [
      { name: 'Carbonated Water', description: 'Pure sparkling water base' },
      { name: 'Sugar', description: 'Natural sweetener (32g per can)' },
      { name: 'Citric Acid (E330)', description: 'Acidity regulator for balanced taste' },
      { name: 'Lemon Juice Concentrate', description: 'Real lemon juice' },
      { name: 'Natural Flavoring', description: 'Pink lemonade flavor' },
      { name: 'Sodium Benzoate (E211)', description: 'Preservative for freshness' },
      { name: 'Anthocyanins (E163)', description: 'Natural pink color from berries' },
    ],
    nutritionDescription: 'Each 300ml can contains 130 calories with 32g of carbohydrates. A sweet and tangy pink lemonade treat.',
    nutritionFacts: [
      { label: 'Serving Size', value: '1 can (300ml / 10.14 fl oz)' },
      { label: 'Calories', value: '130', unit: ' kcal' },
      { label: 'Protein', value: '0.1', unit: 'g' },
      { label: 'Carbohydrate', value: '32', unit: 'g' },
      { label: 'Sugars', value: '32', unit: 'g' },
      { label: 'Fat', value: '0', unit: 'g' },
      { label: 'Saturated Fat', value: '0', unit: 'g' },
      { label: 'Fiber', value: '0.1', unit: 'g' },
      { label: 'Sodium', value: '0.015', unit: 'g' },
    ],
    calories: 130,
    servingSize: '300ml',
    productStats: {
      stat1Value: '130',
      stat1Label: 'Calories/Can',
      stat2Value: '32g',
      stat2Label: 'Sugar/Can',
      stat3Value: '300ml',
      stat3Label: 'Per Can',
      hasArtificialColors: false,
      hasAddedSugar: true,
    },
    reviews: [
      { name: 'Samantha T.', rating: 5, text: 'The prettiest drink with the best taste! Sweet, tangy, and perfectly pink!', location: 'Nashville, TN' },
      { name: 'Brandon L.', rating: 5, text: 'Tastes like summer! The pink lemonade flavor is spot on and so refreshing.', location: 'Atlanta, GA' },
      { name: 'Olivia M.', rating: 5, text: 'Love the natural pink color and the perfect lemonade taste. My kids love it too!', location: 'Phoenix, AZ' },
      { name: 'Derek H.', rating: 4, text: 'Sweet and citrusy with a beautiful color. Great poolside drink!', location: 'San Diego, CA' },
    ],
    faqs: [
      { question: 'What makes G Pink Lemonade different from other lemonade beverages?', answer: 'G Pink Lemonade features real lemon juice concentrate with natural anthocyanin colors from berries. Each 300ml can contains 130 calories.' },
      { question: 'Is G Pink Lemonade suitable for people with dietary restrictions?', answer: 'G Pink Lemonade contains sugar (32g per can) and uses natural colors. Always check the ingredient list if you have specific allergies or dietary concerns.' },
      { question: 'Where can I buy G Pink Lemonade?', answer: 'G Pink Lemonade is available at select retailers nationwide and through our online store. Visit our "Where to Buy" section or contact DLC distribution.' },
      { question: 'How should I store G Pink Lemonade?', answer: 'For the best taste experience, store G Pink Lemonade in a cool, dry place. Refrigerate before drinking for maximum refreshment.' },
      { question: 'What is the serving size?', answer: 'Each can is 300ml (10.14 fl oz) and contains 130 calories with 32g of sugar.' },
      { question: 'Who distributes G Pink Lemonade?', answer: 'G Pink Lemonade is proudly distributed by DLC, a trusted name in premium food and beverage distribution.' },
    ],
  },
  {
    id: 'lemon-lime',
    name: 'G',
    subtitle: 'LEMON & LIME',
    description: 'A classic, refreshing lemon-lime drink with a citrus burst and crisp finish, distributed by DLC.',
    themeColor: '204 100% 85%',
    frameBaseUrl: 'https://munwvcsgdbxjtmgokqmi.supabase.co/storage/v1/object/public/Lemon%20Lime%20Frames/ezgif-split%20(2)/',
    frameDelayPattern: '0.041s',
    totalFrames: 192,
    productImage: gLemonLimeProduct,
    price: 4,
    ingredients: [
      { name: 'Carbonated Water', description: 'Pure sparkling water base' },
      { name: 'Sugar', description: 'Natural sweetener (28g per can)' },
      { name: 'Citric Acid (E330)', description: 'Acidity regulator for balanced taste' },
      { name: 'Lemon Juice Concentrate', description: 'Real lemon juice' },
      { name: 'Lime Juice Concentrate', description: 'Real lime juice' },
      { name: 'Natural Flavoring', description: 'Citrus flavor blend' },
      { name: 'Sodium Benzoate (E211)', description: 'Preservative for freshness' },
    ],
    nutritionDescription: 'Each 300ml can contains 115 calories with 28g of carbohydrates. A classic citrus refreshment with real lemon and lime.',
    nutritionFacts: [
      { label: 'Serving Size', value: '1 can (300ml / 10.14 fl oz)' },
      { label: 'Calories', value: '115', unit: ' kcal' },
      { label: 'Protein', value: '0.1', unit: 'g' },
      { label: 'Carbohydrate', value: '28', unit: 'g' },
      { label: 'Sugars', value: '28', unit: 'g' },
      { label: 'Fat', value: '0', unit: 'g' },
      { label: 'Saturated Fat', value: '0', unit: 'g' },
      { label: 'Fiber', value: '0.1', unit: 'g' },
      { label: 'Sodium', value: '0.012', unit: 'g' },
    ],
    calories: 115,
    servingSize: '300ml',
    productStats: {
      stat1Value: '115',
      stat1Label: 'Calories/Can',
      stat2Value: '28g',
      stat2Label: 'Sugar/Can',
      stat3Value: '300ml',
      stat3Label: 'Per Can',
      hasArtificialColors: false,
      hasAddedSugar: true,
    },
    reviews: [
      { name: 'Nathan R.', rating: 5, text: 'The classic lemon-lime taste done perfectly! Crisp, refreshing, and not too sweet.', location: 'Dallas, TX' },
      { name: 'Victoria S.', rating: 5, text: 'Love the real citrus flavor. The dark green can looks so premium!', location: 'Austin, TX' },
      { name: 'Paul J.', rating: 5, text: 'Perfect balance of lemon and lime. My go-to refreshment for hot days.', location: 'Houston, TX' },
      { name: 'Maria G.', rating: 4, text: 'Refreshingly citrusy with authentic lemon-lime taste. Great quality!', location: 'San Antonio, TX' },
    ],
    faqs: [
      { question: 'What makes G Lemon & Lime different from other lemon-lime beverages?', answer: 'G Lemon & Lime is made with real lemon and lime juice concentrates for an authentic citrus taste. Each 300ml can contains 115 calories.' },
      { question: 'Is G Lemon & Lime suitable for people with dietary restrictions?', answer: 'G Lemon & Lime contains sugar (28g per can). Always check the ingredient list if you have specific allergies or dietary concerns.' },
      { question: 'Where can I buy G Lemon & Lime?', answer: 'G Lemon & Lime is available at select retailers nationwide and through our online store. Visit our "Where to Buy" section or contact DLC distribution.' },
      { question: 'How should I store G Lemon & Lime?', answer: 'For the best taste experience, store G Lemon & Lime in a cool, dry place. Refrigerate before drinking for maximum refreshment.' },
      { question: 'What is the serving size?', answer: 'Each can is 300ml (10.14 fl oz) and contains 115 calories with 28g of sugar.' },
      { question: 'Who distributes G Lemon & Lime?', answer: 'G Lemon & Lime is proudly distributed by DLC, a trusted name in premium food and beverage distribution.' },
    ],
  },
  {
    id: 'cherry-pop',
    name: 'G',
    subtitle: 'CHERRY POP',
    description: 'A bold, refreshing sugar-free cherry drink with a sweet pop of flavor and vibrant taste, distributed by DLC.',
    themeColor: '350 65% 45%',
    frameBaseUrl: 'https://munwvcsgdbxjtmgokqmi.supabase.co/storage/v1/object/public/Cherry%20Pop%20Frames/ezgif-split%20(1)/',
    frameDelayPattern: '0.041s',
    totalFrames: 192,
    productImage: gCherryPopProduct,
    price: 4,
    ingredients: [
      { name: 'Carbonated Water', description: 'Pure sparkling water base' },
      { name: 'Citric Acid (E330)', description: 'Acidity regulator for balanced taste' },
      { name: 'Sucralose (E955) & Acesulfame K (E950)', description: 'Zero-calorie sweeteners' },
      { name: 'Cherry Flavoring', description: 'Natural cherry essence' },
      { name: 'Sodium Benzoate (E211)', description: 'Preservative for freshness' },
    ],
    nutritionDescription: 'Each 300ml can contains only 2.7 calories with virtually zero sugar. A guilt-free, sweet cherry treat.',
    nutritionFacts: [
      { label: 'Serving Size', value: '1 can (300ml / 10.14 fl oz)' },
      { label: 'Calories', value: '2.7', unit: ' kcal' },
      { label: 'Protein', value: '0.03', unit: 'g' },
      { label: 'Carbohydrate', value: '0.09', unit: 'g' },
      { label: 'Sugars', value: '0.03', unit: 'g' },
      { label: 'Fat', value: '0', unit: 'g' },
      { label: 'Saturated Fat', value: '0.03', unit: 'g' },
      { label: 'Fiber', value: '0.009', unit: 'g' },
      { label: 'Sodium', value: '0', unit: 'g' },
    ],
    calories: 3,
    servingSize: '300ml',
    productStats: {
      stat1Value: '3',
      stat1Label: 'Calories/Can',
      stat2Value: '0g',
      stat2Label: 'Sugar Free',
      stat3Value: '300ml',
      stat3Label: 'Per Can',
      hasArtificialColors: false,
      hasAddedSugar: false,
    },
    reviews: [
      { name: 'Ashley T.', rating: 5, text: 'Cherry Pop is the perfect name! Sweet, bold cherry flavor with zero sugar guilt!', location: 'Atlanta, GA' },
      { name: 'Derek M.', rating: 5, text: 'Finally a sugar-free cherry drink that actually tastes like real cherries. Love it!', location: 'Charlotte, NC' },
      { name: 'Megan R.', rating: 5, text: 'The cherry flavor pops! So refreshing and perfect for summer. My new favorite!', location: 'Orlando, FL' },
      { name: 'Tyler B.', rating: 4, text: 'Great cherry taste without the sugar. Perfect for anyone watching their calories!', location: 'Nashville, TN' },
    ],
    faqs: [
      { question: 'What makes G Cherry Pop different from other cherry beverages?', answer: 'G Cherry Pop is completely sugar-free with only 3 calories per 300ml can, sweetened with sucralose and acesulfame K for a guilt-free cherry experience.' },
      { question: 'Is G Cherry Pop suitable for people with dietary restrictions?', answer: 'G Cherry Pop is sugar-free and contains only 3 calories per can. It uses artificial sweeteners (sucralose E955, acesulfame K E950). Always check the ingredient list if you have specific allergies or dietary concerns.' },
      { question: 'Where can I buy G Cherry Pop?', answer: 'G Cherry Pop is available at select retailers nationwide and through our online store. Visit our "Where to Buy" section or contact DLC distribution.' },
      { question: 'How should I store G Cherry Pop?', answer: 'For the best taste experience, store G Cherry Pop in a cool, dry place. Refrigerate before drinking for maximum refreshment.' },
      { question: 'What is the serving size?', answer: 'Each can is 300ml (10.14 fl oz) and contains only 3 calories with zero sugar.' },
      { question: 'Who distributes G Cherry Pop?', answer: 'G Cherry Pop is proudly distributed by DLC, a trusted name in premium food and beverage distribution.' },
    ],
  },
  {
    id: 'lemon-lime-sf',
    name: 'G',
    subtitle: 'LEMON & LIME SF',
    description: 'A crisp, refreshing sugar-free lemon and lime drink with authentic citrus flavor, distributed by DLC.',
    themeColor: '75 70% 50%',
    frameBaseUrl: 'https://munwvcsgdbxjtmgokqmi.supabase.co/storage/v1/object/public/Lemon%20Lime%20(SF)%20Frames/ezgif-split/',
    frameDelayPattern: '0.041s',
    totalFrames: 192,
    productImage: gLemonLimeSfProduct,
    price: 4,
    ingredients: [
      { name: 'Carbonated Water', description: 'Pure sparkling water base' },
      { name: 'Citric Acid (E330)', description: 'Acidity regulator for balanced taste' },
      { name: 'Sucralose (E955) & Acesulfame K (E950)', description: 'Zero-calorie sweeteners' },
      { name: 'Lemon Juice Concentrate', description: 'Real lemon juice' },
      { name: 'Lime Juice Concentrate', description: 'Real lime juice' },
      { name: 'Natural Flavoring', description: 'Citrus flavor blend' },
      { name: 'Sodium Benzoate (E211)', description: 'Preservative for freshness' },
    ],
    nutritionDescription: 'Each 300ml can contains only 2.1 calories with virtually zero sugar. A guilt-free citrus refreshment.',
    nutritionFacts: [
      { label: 'Serving Size', value: '1 can (300ml / 10.14 fl oz)' },
      { label: 'Calories', value: '2.1', unit: ' kcal' },
      { label: 'Protein', value: '0.03', unit: 'g' },
      { label: 'Carbohydrate', value: '0.09', unit: 'g' },
      { label: 'Sugars', value: '0.03', unit: 'g' },
      { label: 'Fat', value: '0.03', unit: 'g' },
      { label: 'Saturated Fat', value: '0.003', unit: 'g' },
      { label: 'Fiber', value: '0.06', unit: 'g' },
      { label: 'Sodium', value: '0.063', unit: 'g' },
    ],
    calories: 2,
    servingSize: '300ml',
    productStats: {
      stat1Value: '2',
      stat1Label: 'Calories/Can',
      stat2Value: '0g',
      stat2Label: 'Sugar Free',
      stat3Value: '300ml',
      stat3Label: 'Per Can',
      hasArtificialColors: false,
      hasAddedSugar: false,
    },
    reviews: [
      { name: 'Kevin L.', rating: 5, text: 'All the lemon-lime flavor with zero sugar! This is my new go-to refreshment!', location: 'Seattle, WA' },
      { name: 'Jennifer S.', rating: 5, text: 'Finally a sugar-free lemon-lime that tastes like the real thing. Absolutely love it!', location: 'Portland, OR' },
      { name: 'Ryan P.', rating: 5, text: 'Crisp citrus taste without the calories. Perfect for staying refreshed guilt-free!', location: 'Denver, CO' },
      { name: 'Amanda K.', rating: 4, text: 'Great alternative to sugary sodas. The lemon-lime flavor is spot on!', location: 'San Francisco, CA' },
    ],
    faqs: [
      { question: 'What makes G Lemon & Lime SF different from the regular version?', answer: 'G Lemon & Lime SF is the sugar-free version with only 2 calories per 300ml can compared to 115 calories in the regular version, sweetened with sucralose and acesulfame K.' },
      { question: 'Is G Lemon & Lime SF suitable for people with dietary restrictions?', answer: 'G Lemon & Lime SF is sugar-free and contains only 2 calories per can. It uses artificial sweeteners (sucralose E955, acesulfame K E950). Always check the ingredient list if you have specific allergies or dietary concerns.' },
      { question: 'Where can I buy G Lemon & Lime SF?', answer: 'G Lemon & Lime SF is available at select retailers nationwide and through our online store. Visit our "Where to Buy" section or contact DLC distribution.' },
      { question: 'How should I store G Lemon & Lime SF?', answer: 'For the best taste experience, store G Lemon & Lime SF in a cool, dry place. Refrigerate before drinking for maximum refreshment.' },
      { question: 'What is the serving size?', answer: 'Each can is 300ml (10.14 fl oz) and contains only 2 calories with zero sugar.' },
      { question: 'Who distributes G Lemon & Lime SF?', answer: 'G Lemon & Lime SF is proudly distributed by DLC, a trusted name in premium food and beverage distribution.' },
    ],
  },
];
